/**
 * Collapse a repeating scanner fault into a single report per session.
 *
 * The ZXing decode callback fires on every frame (~30x a second), so a fault
 * that is a property of the DEVICE rather than of one frame — the camera
 * rejecting an applied constraint (`UnknownError: setPhotoOptions failed`), a
 * track that died mid-scan — is raised again and again for as long as the
 * operator keeps the scanner open. Forwarded straight to Sentry that is
 * hundreds of identical events from one scan: quota burned, and the faults we
 * have NOT seen before buried under them.
 *
 * Keying on the fault's identity (type + message) keeps the first occurrence
 * of every distinct problem, including ones that only appear after another
 * fault has already been reported, and drops only the verbatim repeats.
 */
function signature(err: unknown): string {
    if (err instanceof Error) return `${err.name}: ${err.message}`
    if (typeof err === 'string') return err
    try {
        return JSON.stringify(err) ?? String(err)
    } catch {
        // Circular or otherwise unserializable — treat every such value as the
        // same fault rather than letting the deduper throw into the scan loop.
        return 'unserializable'
    }
}

export function createErrorDeduper(
    report: (err: unknown) => void,
): (err: unknown) => void {
    const reported = new Set<string>()
    return (err: unknown) => {
        const key = signature(err)
        if (reported.has(key)) return
        reported.add(key)
        report(err)
    }
}
